import { FormEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Field,
  Fieldset,
  Heading,
  Input,
  Label,
  Paragraph,
  Radio,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { DiscussionError, DiscussionRequest } from '../../../api/workEnvironmentTypes';
import {
  useAddDiscussionMutation,
  useRemoveDiscussionMutation,
} from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { useProposalDiscussionsQuery } from '../../../hooks/queries/useWorkEnvironmentMeasuresQuery';
import { useSurveyActions } from '../../../hooks/useSurveyActions';
import { FieldError, validationErrors } from '../assessmentView';
import { today } from '../dataSourcesView';
import {
  DISCUSSION_FIELDS,
  DISCUSSION_FORUMS,
  discussionErrors,
  discussionFieldErrors,
  discussionLabel,
  newDiscussion,
} from '../discussionsView';
import { FormErrorSummary, TextField } from '../components/FormParts';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

/** Where and when the team discussed the proposals, so the documentation for inspection shows it */
export const DiscussionsCard = ({ surveyId }: { surveyId: number }): ReactElement | null => {
  const { t, i18n } = useTranslation();
  const { may } = useSurveyActions();
  const { data } = useProposalDiscussionsQuery(surveyId);
  const add = useAddDiscussionMutation(surveyId);
  const remove = useRemoveDiscussionMutation(surveyId);
  const [form, setForm] = useState<DiscussionRequest | null>(null);
  const [errors, setErrors] = useState<FieldError[]>([]);
  if (!data) return null;
  const canRegister = data.canRegister && may('makePlan');
  const errorFor = (fieldId: string) => errors.find((error) => error.fieldId === fieldId)?.message;

  const close = () => {
    setForm(null);
    setErrors([]);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!form) return;
    const invalid = discussionErrors(form);
    if (invalid.length > 0) return setErrors(discussionFieldErrors(invalid, t));
    add.mutate(form, {
      onSuccess: close,
      onError: (error) => {
        const fromApi = validationErrors<DiscussionError>(error);
        if (fromApi) setErrors(discussionFieldErrors(fromApi, t));
      },
    });
  };

  return (
    <section aria-labelledby="discussions-heading" className={`${process.card} ${process.sideCard}`}>
      <Heading level={2} data-size="xs" id="discussions-heading">
        {t('workEnvironmentMeasures.discussions.title')}
      </Heading>
      <Paragraph data-size="sm" className={process.subtle}>
        {t('workEnvironmentMeasures.discussions.intro')}
      </Paragraph>
      {data.discussions.length === 0 ? (
        <Paragraph data-size="sm">{t('workEnvironmentMeasures.discussions.empty')}</Paragraph>
      ) : (
        <ul className={classes.log}>
          {data.discussions.map((discussion) => {
            const label = discussionLabel(discussion, t, i18n.language);
            return (
              <li key={discussion.id} className={classes.logEntry} data-type="event">
                <Paragraph data-size="sm">{label}</Paragraph>
                {discussion.note && (
                  <Paragraph data-size="xs" className={process.subtle}>
                    {discussion.note}
                  </Paragraph>
                )}
                {canRegister && (
                  <Button
                    type="button"
                    variant="tertiary"
                    data-size="sm"
                    aria-label={t('workEnvironmentMeasures.discussions.remove', { label })}
                    loading={remove.isPending && remove.variables === discussion.id}
                    onClick={() => remove.mutate(discussion.id)}
                  >
                    {t('workEnvironmentMeasures.discussions.removeShort')}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {canRegister && !form && (
        <Button type="button" variant="secondary" data-size="sm" onClick={() => setForm(newDiscussion())}>
          {t('workEnvironmentMeasures.discussions.register')}
        </Button>
      )}
      {form && (
        <form onSubmit={submit} noValidate className={classes.areaBody}>
          <FormErrorSummary errors={errors} />
          <Fieldset data-size="sm">
            <Fieldset.Legend>{t('workEnvironmentMeasures.discussions.forum')}</Fieldset.Legend>
            {DISCUSSION_FORUMS.map((forum) => (
              <Radio
                key={forum}
                id={`discussion-forum-${forum}`}
                name="discussion-forum"
                value={forum}
                label={t(`workEnvironmentMeasures.discussions.forums.${forum}`)}
                checked={form.forum === forum}
                onChange={() => setForm({ ...form, forum })}
              />
            ))}
            {errorFor(DISCUSSION_FIELDS.forum) && <ValidationMessage>{errorFor(DISCUSSION_FIELDS.forum)}</ValidationMessage>}
          </Fieldset>
          <Field data-size="sm">
            <Label htmlFor={DISCUSSION_FIELDS.date}>{t('workEnvironmentMeasures.discussions.date')}</Label>
            <Input
              id={DISCUSSION_FIELDS.date}
              type="date"
              max={today()}
              value={form.date}
              aria-invalid={Boolean(errorFor(DISCUSSION_FIELDS.date)) || undefined}
              onChange={(event) => setForm({ ...form, date: event.target.value })}
            />
            {errorFor(DISCUSSION_FIELDS.date) && <ValidationMessage>{errorFor(DISCUSSION_FIELDS.date)}</ValidationMessage>}
          </Field>
          <TextField
            id={DISCUSSION_FIELDS.note}
            label={t(`workEnvironmentMeasures.discussions.${form.forum === 'OTHER' ? 'noteOther' : 'note'}`)}
            help={t(`workEnvironmentMeasures.discussions.${form.forum === 'OTHER' ? 'noteHelpOther' : 'noteHelp'}`)}
            value={form.note}
            error={errorFor(DISCUSSION_FIELDS.note)}
            onChange={(note) => setForm({ ...form, note })}
          />
          {add.isError && !validationErrors(add.error) && (
            <Alert data-color="danger">
              <Paragraph>{t('workEnvironmentMeasures.discussions.error')}</Paragraph>
            </Alert>
          )}
          <div className={classes.dialogButtons}>
            <Button type="submit" variant="secondary" data-size="sm" loading={add.isPending}>
              {t('workEnvironmentMeasures.discussions.save')}
            </Button>
            <Button type="button" variant="tertiary" data-size="sm" onClick={close}>
              {t('workEnvironmentMeasures.discussions.cancel')}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
};
